import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  message,
  Modal,
  Spin,
  Tooltip,
  Card,
  Tag,
  Avatar,
  Select,
  Space,
  Drawer,
  Descriptions,
  Upload,
  Input as AntInput,
} from "antd";
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  UserOutlined,
  PaperClipOutlined,
  SendOutlined,
  FileTextOutlined,
  MailOutlined, 
  DownloadOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import moment from "moment";

const { Option } = Select;
const { TextArea } = AntInput;
const { confirm } = Modal;

export default function AdminComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
    priority: "all",
    sort: "newest",
  });

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    "in-progress": 0,
    resolved: 0,
  });

  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);

  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [fileList, setFileList] = useState([]);

  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [resolutionText, setResolutionText] = useState("");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const statusColors = {
    pending: "gold",
    "in-progress": "blue",
    resolved: "green",
  };

  const priorityColors = {
    low: "gray",
    medium: "orange",
    high: "red",
    urgent: "volcano",
  };

  const categoryLabels = {
    "product-quality": "Product Quality",
    delivery: "Delivery Issue",
    damaged: "Damaged Item",
    "wrong-item": "Wrong Item",
    "missing-parts": "Missing Parts",
    other: "Other",
  };

  const statusLabel = (status) => {
    if (!status) return "Pending";
    if (status === "in-progress") return "In Progress";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const safeText = (v, fallback = "N/A") => (v ? v : fallback);

  const getAttachmentUrl = (att) => (typeof att === "string" ? att : att?.url);
  const getAttachmentName = (att, idx) => {
    if (typeof att === "string") {
      try {
        const raw = att.split("?")[0];
        const name = decodeURIComponent(raw.split("/").pop() || "");
        return name || `Attachment ${idx + 1}`;
      } catch {
        return `Attachment ${idx + 1}`;
      }
    }
    return att?.name || `Attachment ${idx + 1}`;
  };

  // Helper: safely parse customerInfo JSON and return normalized user object
  const parseCustomerInfo = (customerInfo) => {
    if (!customerInfo) return null;
    try {
      const parsed = typeof customerInfo === "string" ? JSON.parse(customerInfo) : customerInfo;
      return {
        name: parsed.name || parsed.fullName || parsed.customerName || parsed.firstName || "",
        email: parsed.email || parsed.emailAddress || parsed.mail || "",
        phone: parsed.phone || parsed.mobile || parsed.contact || "",
        company: parsed.company || "",
        postalAddress: parsed.postalAddress || parsed.address || "",
      };
    } catch (err) {
      // if parse fails, just return null
      return null;
    }
  };

  // Always return user object (either record.user or parsed from record.customerInfo)
  const getUserFromRecord = (record) => {
    if (!record) return null;
    if (record.user) return record.user;
    const parsed = parseCustomerInfo(record.customerInfo || record.customer);
    return parsed;
  };

  // =========================
  // Fetch complaints (list)
  // =========================
  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...(filters.status !== "all" && { status: filters.status }),
        ...(filters.priority !== "all" && { priority: filters.priority }),
        ...(searchText && { search: searchText }),
        sort: filters.sort,
      };

      const res = await axiosInstance.get("/complaints/admin/all", { params });

      if (res.data?.success) {
        // normalize complaints: ensure complaint.user exists (parse customerInfo if needed)
        // normalize complaints: ensure complaint.user exists (parse customerInfo if needed)
const normalized = (res.data.complaints || []).map((c) => {
  // parse customerInfo (handles JSON string or object)
  let parsedCustomer = null;
  try {
    const raw = c.customerInfo || c.customer || null;
    parsedCustomer = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : null;
  } catch (e) {
    parsedCustomer = null;
  }

  // build user-like object: prefer joined user, otherwise fallback to customerInfo
  const userObj = c.user
    ? {
        id: c.user.id,
        name: c.user.full_name || c.user.name || c.user.company_name || c.user.email || "",
        company: c.user.company_name || c.user.company || "",
        email: c.user.email || "",
        phone: c.user.phone || "",
      }
    : parsedCustomer
    ? {
        id: null,
        name: parsedCustomer.name || parsedCustomer.fullName || parsedCustomer.customerName || "",
        company: parsedCustomer.company || "",
        email: parsedCustomer.email || "",
        phone: parsedCustomer.phone || "",
      }
    : { name: c.customerName || "Unknown" };

  return {
    ...c,
    customerInfo: parsedCustomer, // expose parsed customerInfo for drawer/details
    user: userObj,
  };
});


        setComplaints(normalized);
        setPagination((prev) => ({
          ...prev,
          total: res.data.pagination?.total || 0,
        }));

        if (res.data.stats) {
          setStats({
            total: res.data.stats.total || 0,
            pending: res.data.stats.pending || 0,
            "in-progress": res.data.stats["in-progress"] || 0,
            resolved: res.data.stats.resolved || 0,
          });
        }
      } else {
        setComplaints([]);
        message.error("Failed to load complaints");
      }
    } catch (err) {
      console.error(err);
      message.error("An error occurred while fetching complaints.");
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Fetch stats (overview)
  // =========================
  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get("/complaints/stats/overview");
      if (res.data?.success) {
        const s = res.data.stats?.overall || res.data.stats || {};
        setStats({
          total: s.total || 0,
          pending: s.pending || 0,
          "in-progress": s["in-progress"] || 0,
          resolved: s.resolved || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchComplaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters, searchText]);

  // =========================
  // View complaint details
  // =========================
  const viewComplaintDetails = async (id) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/complaints/admin/${id}`);

      if (res.data?.success) {
  const c = res.data.complaint || {};

  // parse customerInfo (backend should already normalize but be defensive)
  let parsedCustomer = null;
  try {
    const raw = c.customerInfo || c.customer || null;
    parsedCustomer = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : null;
  } catch (e) {
    parsedCustomer = null;
  }

  // build user object (prefer joined user, fallback to customerInfo)
  const userObj = c.user
    ? {
        id: c.user.id,
        name: c.user.full_name || c.user.name || c.user.company_name || c.user.email || "",
        company: c.user.company_name || c.user.company || "",
        email: c.user.email || "",
        phone: c.user.phone || "",
      }
    : parsedCustomer
    ? {
        id: null,
        name: parsedCustomer.name || parsedCustomer.fullName || parsedCustomer.customerName || "",
        company: parsedCustomer.company || "",
        email: parsedCustomer.email || "",
        phone: parsedCustomer.phone || "",
      }
    : null;

  const normalized = {
    ...c,
    customerInfo: parsedCustomer,
    user: userObj,
  };

  setSelectedComplaint(normalized);
  setDetailDrawerVisible(true);
}
 else {
        message.error("Failed to load complaint details");
      }
    } catch (err) {
      console.error(err);
      message.error("Error loading complaint details");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Send message (admin)
  // =========================
  const sendMessage = async () => {
    if (!messageText.trim()) {
      message.warning("Please enter a message");
      return;
    }

    if (!selectedComplaint?.id) {
      message.error("No complaint selected");
      return;
    }

    try {
      setSendingMessage(true);

      const formData = new FormData();
      formData.append("message", messageText.trim());

      fileList.forEach((file) => {
        if (file?.originFileObj) {
          formData.append("attachments", file.originFileObj);
        }
      });

      const res = await axiosInstance.post(
        `/complaints/admin/${selectedComplaint.id}/messages`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data?.success) {
        message.success("Message sent successfully");
        setMessageText("");
        setFileList([]);

        const refreshRes = await axiosInstance.get(
          `/complaints/admin/${selectedComplaint.id}`
        );
        if (refreshRes.data?.success) {
          const refreshed = { ...refreshRes.data.complaint, user: getUserFromRecord(refreshRes.data.complaint) || refreshRes.data.complaint.user };
          setSelectedComplaint(refreshed);
        }

        fetchComplaints();
      } else {
        message.error(res.data?.message || "Failed to send message");
      }
    } catch (err) {
      console.error(err);
      message.error("Network error while sending message");
    } finally {
      setSendingMessage(false);
    }
  };

  // =========================
  // Update status (admin)
  // =========================
  const updateStatus = async (id, status, resolution = "") => {
    if (!id || !status) return;

    if (status === "resolved" && !resolution.trim()) {
      message.warning("Please enter resolution details");
      return;
    }

    try {
      setLoading(true);
      const res = await axiosInstance.put(`/complaints/admin/${id}/status`, {
        status,
        ...(resolution?.trim() && { resolution: resolution.trim() }),
      });

      if (res.data?.success) {
        message.success(`Complaint ${statusLabel(status)} successfully`);

        setStatusModalVisible(false);
        setResolutionText("");
        setNewStatus("");

        fetchComplaints();
        fetchStats();

        if (detailDrawerVisible) {
          const refreshRes = await axiosInstance.get(`/complaints/admin/${id}`);
          if (refreshRes.data?.success) {
            setSelectedComplaint({ ...refreshRes.data.complaint, user: getUserFromRecord(refreshRes.data.complaint) || refreshRes.data.complaint.user });
          }
        }
      } else {
        message.error(res.data?.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      message.error("Network error while updating status");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Delete complaint (admin)
  // =========================
  const handleDelete = (id) => {
    confirm({
      title: "Delete this complaint?",
      icon: <ExclamationCircleOutlined />,
      content: "This will permanently delete the complaint and all associated messages.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const res = await axiosInstance.delete(`/complaints/admin/${id}`);

          if (res.data?.success) {
            message.success("Complaint deleted successfully");

            if (selectedComplaint?.id === id) {
              setDetailDrawerVisible(false);
              setSelectedComplaint(null);
            }

            fetchComplaints();
            fetchStats();
          } else {
            message.error(res.data?.message || "Failed to delete complaint");
          }
        } catch (err) {
          console.error(err);
          message.error("Network error while deleting complaint");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // =========================
  // Table pagination change
  // =========================
  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  // =========================
  // Columns
  // =========================
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (text) => <div className="font-semibold text-gray-900">#{text}</div>,
    },
    {
      title: "Customer",
      key: "user",
      render: (_, record) => {
        const u = getUserFromRecord(record) || record.user || {};
        const name = u?.name || u?.fullName || u?.email || "N/A";
        return (
          <div className="flex items-center space-x-2">
            <Avatar size="small" icon={<UserOutlined />} />
            <div>
              <div className="font-medium text-gray-900">{safeText(name)}</div>
              <div className="text-xs text-gray-500">{u?.company || ""}</div>
              <div className="text-xs text-gray-400">
                {u?.email ? u.email : u?.phone ? u.phone : ""}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
      render: (text) => <div className="font-medium text-gray-900">{safeText(text)}</div>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => (
        <Tag color="blue" className="rounded-full">
          {categoryLabels[category] || category || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={statusColors[status] || "default"} className="rounded-full">
          {statusLabel(status)}
        </Tag>
      ),
    },
    {
      title: "Order",
      dataIndex: "orderReference",
      key: "orderReference",
      render: (ref) => (
        <Tag className="bg-gray-100 text-gray-800 rounded-full">{ref || "N/A"}</Tag>
      ),
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => (
        <div className="text-gray-600">{date ? moment(date).fromNow() : "N/A"}</div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      align: "center",
      render: (_, record) => {
        const currentStatus = record.status || "pending";
        return (
          <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center" }}>
            <Tooltip title="View Details">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => viewComplaintDetails(record.id)}
              />
            </Tooltip>

            <Tooltip title="Open & Reply">
              <Button
                icon={<MessageOutlined />}
                size="small"
                onClick={() => viewComplaintDetails(record.id)}
              />
            </Tooltip>

            <Select
              size="small"
              style={{ width: 140 }}
              value={currentStatus}
              onChange={(value) => {
                if (value === "delete") {
                  handleDelete(record.id);
                } else if (["resolved", "in-progress"].includes(value)) {
                  setNewStatus(value);
                  // ensure selectedComplaint is full record before opening the modal
                  setSelectedComplaint((prev) => (prev && prev.id === record.id ? prev : record));
                  setStatusModalVisible(true);
                }
              }}
            >
              <Option value="pending">Pending</Option>
              <Option value="in-progress">In Progress</Option>
              <Option value="resolved">Resolved</Option>
              <Option value="delete" className="text-red-500">
                Delete
              </Option>
            </Select>
          </div>
        );
      },
    },
  ];

  return (
    <Spin spinning={loading} size="large">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Complaint Management</h1>
                <p className="text-gray-600">Manage and respond to customer complaints</p>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="shadow-sm border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <FileTextOutlined className="text-blue-600 text-xl" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                  <div className="text-gray-600">Total</div>
                </div>
              </div>
            </Card>

            <Card className="shadow-sm border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <ClockCircleOutlined className="text-yellow-600 text-xl" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.pending}</div>
                  <div className="text-gray-600">Pending</div>
                </div>
              </div>
            </Card>

            <Card className="shadow-sm border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <SyncOutlined className="text-blue-600 text-xl" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats["in-progress"]}</div>
                  <div className="text-gray-600">In Progress</div>
                </div>
              </div>
            </Card>

            <Card className="shadow-sm border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-green-100 p-3 rounded-lg">
                  <CheckCircleOutlined className="text-green-600 text-xl" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.resolved}</div>
                  <div className="text-gray-600">Resolved</div>
                </div>
              </div>
            </Card>
          </div>

        {/* Filters (Export button restored & equal widths) */}
{/* Filters */}
<div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    
    {/* Search */}
    <div className="flex flex-col w-full">
      <label className="text-sm font-medium text-gray-700 mb-2">
        Search complaints
      </label>
      <Input
        placeholder="Search by subject, customer, or order..."
        value={searchText}
        onChange={(e) => {
          setPagination((p) => ({ ...p, current: 1 }));
          setSearchText(e.target.value);
        }}
        allowClear
        className="h-10 rounded-lg w-full"
      />
    </div>

    {/* Status */}
    <div className="flex flex-col w-full">
      <label className="text-sm font-medium text-gray-700 mb-2">Status</label>
      <Select
        value={filters.status}
        onChange={(value) => {
          setPagination((p) => ({ ...p, current: 1 }));
          setFilters((prev) => ({ ...prev, status: value }));
        }}
        className="h-10 rounded-lg w-full"
      >
        <Option value="all">All</Option>
        <Option value="pending">Pending</Option>
        <Option value="in-progress">In Progress</Option>
        <Option value="resolved">Resolved</Option>
      </Select>
    </div>

    {/* Sort */}
    <div className="flex flex-col w-full">
      <label className="text-sm font-medium text-gray-700 mb-2">Sort By</label>
      <Select
        value={filters.sort}
        onChange={(value) => {
          setPagination((p) => ({ ...p, current: 1 }));
          setFilters((prev) => ({ ...prev, sort: value }));
        }}
        className="h-10 rounded-lg w-full"
      >
        <Option value="newest">Newest First</Option>
        <Option value="oldest">Oldest First</Option>
      </Select>
    </div>

  </div>
</div>


          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={complaints}
              columns={columns}
              rowKey="id"
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} complaints`,
              }}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              className="modern-orders-table"
            />
          </div>
        </div>
      </div>

      {/* Drawer */}
      <Drawer
        title={
          <div className="flex items-center space-x-2">
            <ExclamationCircleOutlined className="text-red-500" />
            <span>Complaint Details</span>
            {selectedComplaint?.status && (
              <Tag color={statusColors[selectedComplaint.status] || "default"} className="ml-2">
                {statusLabel(selectedComplaint.status)}
              </Tag>
            )}
          </div>
        }
        width={700}
        open={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false);
          setSelectedComplaint(null);
          setMessageText("");
          setFileList([]);
          setNewStatus("");
          setResolutionText("");
          setStatusModalVisible(false);
        }}
        destroyOnClose
        extra={
          selectedComplaint && (
            <Space>
              <Select
                value={selectedComplaint.status || "pending"}
                onChange={(value) => {
                  setNewStatus(value);
                  setStatusModalVisible(true);
                }}
                style={{ width: 160 }}
              >
                <Option value="in-progress">In Progress</Option>
                <Option value="resolved">Resolved</Option>
              </Select>
              <Button onClick={() => setDetailDrawerVisible(false)}>Close</Button>
            </Space>
          )
        }
      >
        {selectedComplaint && (
          <div className="space-y-6">
            {/* Basic */}
            <Card size="small">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Complaint ID">
                  <strong>#{selectedComplaint.id}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Priority">
                  <Tag color={priorityColors[selectedComplaint.priority] || "default"}>
                    {selectedComplaint.priority || "N/A"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Category">
                  {categoryLabels[selectedComplaint.category] ||
                    selectedComplaint.category ||
                    "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {selectedComplaint.createdAt
                    ? moment(selectedComplaint.createdAt).format("YYYY-MM-DD HH:mm")
                    : "N/A"}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Customer */}
           {/* Reporter (account/user who created complaint) */}
<Card size="small" title="Reported By (Account)">
  <div className="flex items-center space-x-3 mb-3">
    <Avatar size="large" icon={<UserOutlined />} />
    <div>
      <div className="font-semibold">{selectedComplaint.user?.name || "N/A"}</div>
      <div className="text-gray-600">{selectedComplaint.user?.company || ""}</div>

      <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
        {selectedComplaint.user?.email && (
          <a href={`mailto:${selectedComplaint.user.email}`} className="inline-flex items-center gap-1">
            <MailOutlined /> {selectedComplaint.user.email}
          </a>
        )}
        {selectedComplaint.user?.phone && (
          <a href={`tel:${selectedComplaint.user.phone}`} className="inline-flex items-center gap-1">
            <PhoneOutlined /> {selectedComplaint.user.phone}
          </a>
        )}
      </div>
    </div>
  </div>
</Card>

{/* Customer Info (the customer this complaint is about) */}
{selectedComplaint.customerInfo && (
  <Card size="small" title="Customer Information">
    <div className="flex items-center space-x-3 mb-3">
      <Avatar size="large" icon={<UserOutlined />} />
      <div>
        <div className="font-semibold">
          {selectedComplaint.customerInfo.name || "N/A"}
        </div>
        <div className="text-gray-600">{selectedComplaint.customerInfo.company || ""}</div>

        <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
          {selectedComplaint.customerInfo.email && (
            <a href={`mailto:${selectedComplaint.customerInfo.email}`} className="inline-flex items-center gap-1">
              <MailOutlined /> {selectedComplaint.customerInfo.email}
            </a>
          )}
          {selectedComplaint.customerInfo.phone && (
            <a href={`tel:${selectedComplaint.customerInfo.phone}`} className="inline-flex items-center gap-1">
              <PhoneOutlined /> {selectedComplaint.customerInfo.phone}
            </a>
          )}
        </div>

        {selectedComplaint.customerInfo.postalAddress && (
          <div className="text-sm text-gray-500 mt-2">
            {selectedComplaint.customerInfo.postalAddress}
          </div>
        )}
      </div>
    </div>
  </Card>
)}


            {/* Order */}
            <Card size="small" title="Order Information">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Order Reference">
                  {selectedComplaint.order?.referenceNumber ||
                    selectedComplaint.orderReference ||
                    "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Tracking ID">
                  {selectedComplaint.order?.trackingId || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Product">
                  {selectedComplaint.order?.productName || "N/A"}
                </Descriptions.Item>
                {selectedComplaint.order?.size && (
                  <Descriptions.Item label="Size">{selectedComplaint.order.size}</Descriptions.Item>
                )}
                {selectedComplaint.order?.fabric && (
                  <Descriptions.Item label="Fabric">{selectedComplaint.order.fabric}</Descriptions.Item>
                )}
                {selectedComplaint.order?.fabricColor && (
                  <Descriptions.Item label="Fabric Color">
                    {selectedComplaint.order.fabricColor}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Description */}
            <Card size="small" title="Description">
              <p className="text-gray-700">{safeText(selectedComplaint.description, "")}</p>
            </Card>

            {/* Attachments */}
            {Array.isArray(selectedComplaint.attachments) &&
              selectedComplaint.attachments.length > 0 && (
                <Card size="small" title="Attachments">
                  <Space wrap>
                    {selectedComplaint.attachments.map((att, index) => {
                      const url = getAttachmentUrl(att);
                      const name = getAttachmentName(att, index);
                      if (!url) return null;

                      return (
                        <Button
                          key={index}
                          icon={<PaperClipOutlined />}
                          type="link"
                          href={url}
                          target="_blank"
                        >
                          {name}
                        </Button>
                      );
                    })}
                  </Space>
                </Card>
              )}

            {/* Conversation */}
            <Card size="small" title="Conversation">
              <div className="max-h-64 overflow-y-auto space-y-4 mb-4">
                {(selectedComplaint.messages || []).map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`p-3 rounded-lg ${
                      msg.sender === "support"
                        ? "bg-blue-50 border-l-4 border-blue-500"
                        : "bg-gray-50 border-l-4 border-gray-400"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold">
                        {msg.sender === "support" ? "Support Team" : "Customer"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {msg.createdAt ? moment(msg.createdAt).format("MMM D, HH:mm") : ""}
                      </div>
                    </div>

                    <p className="text-gray-700">{safeText(msg.message, "")}</p>

                    {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {msg.attachments.map((att, aIdx) => {
                          const url = getAttachmentUrl(att);
                          const name = getAttachmentName(att, aIdx);
                          if (!url) return null;
                          return (
                            <Button
                              key={aIdx}
                              size="small"
                              icon={<PaperClipOutlined />}
                              type="link"
                              href={url}
                              target="_blank"
                            >
                              {name}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Reply */}
              <div className="border-t pt-4">
                <TextArea
                  placeholder="Type your reply here..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  rows={3}
                  className="mb-3"
                />

                <div className="flex justify-between items-center">
                  <Upload
                    fileList={fileList}
                    onChange={({ fileList: fl }) => setFileList(fl)}
                    beforeUpload={() => false}
                    multiple
                    maxCount={5}
                  >
                    <Button icon={<PaperClipOutlined />}>Attach Files</Button>
                  </Upload>

                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={sendingMessage}
                    onClick={sendMessage}
                  >
                    Send Reply
                  </Button>
                </div>
              </div>
            </Card>

            {/* Resolution */}
            {selectedComplaint.resolution && (
              <Card size="small" title="Resolution" className="bg-green-50">
                <p className="text-green-700">{selectedComplaint.resolution}</p>
                {selectedComplaint.resolvedAt && (
                  <div className="text-sm text-green-600 mt-2">
                    Resolved on {moment(selectedComplaint.resolvedAt).format("YYYY-MM-DD")}
                  </div>
                )}
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* Status Modal */}
      <Modal
        title={`Update Status: ${statusLabel(newStatus)}`}
        open={statusModalVisible}
        onCancel={() => {
          setStatusModalVisible(false);
          setNewStatus("");
          setResolutionText("");
        }}
        onOk={() => {
          if (!selectedComplaint?.id) return;
          updateStatus(selectedComplaint.id, newStatus, resolutionText);
        }}
        okText="Update"
        cancelText="Cancel"
        destroyOnClose
      >
        {newStatus === "resolved" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resolution Details
            </label>
            <TextArea
              rows={4}
              placeholder="Enter resolution details..."
              value={resolutionText}
              onChange={(e) => setResolutionText(e.target.value)}
            />
          </div>
        )}
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }
        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }
        .modern-orders-table .ant-table-tbody > tr:hover > td {
          background-color: #f8fafc;
        }
      `}</style>
    </Spin>
  );
}
